/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-inferrable-types */
import { CsbHelper, getConfigValue, globalTrackingService } from "@recruiting-solutions/rs-shared";
import { saveAs } from 'file-saver';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from "firebase/auth";
import { CandidateField } from "../models/candidateField";
import { Question } from "../models";
import { Profile } from "../models/profile";
import { AuthService } from './auth.service';


class ApplyServiceController {
    getCustomerId(): string {
        return getConfigValue('customerId');
    }

    async deleteProfile() {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const deleteProfileFunction = httpsCallable(fireFunctions, 'deleteProfile');

        const result = await deleteProfileFunction({ customerId });

        return result.data;
    }

    async getCandidateFields() {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const getCandidateFieldsFunction = httpsCallable(fireFunctions, 'getCandidateFields');

        const result = await getCandidateFieldsFunction({ customerId });
        return result.data as CandidateField[];
    }

    async getApplicationFields() {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const getApplicationFieldsFunction = httpsCallable(fireFunctions, 'getApplicationFields');

        const result = await getApplicationFieldsFunction({ customerId });
        return result.data as CandidateField[];
    }

    async getApplication(applicationId: string) {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const getJobApplicationFunction = httpsCallable(fireFunctions, 'getJobApplication');

        const result = await getJobApplicationFunction({ customerId, applicationId });

        return result.data;
    }

    async getInternalCandidateId() {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const getInternalCandidateIdFunction = httpsCallable(fireFunctions, 'getInternalCandidateId');
        const result = await getInternalCandidateIdFunction({ customerId });
        return result.data as string;
    }

    async updateUserCandidateIdFromEmail(email: string) {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const updateUserCandidateIdFromEmailFunction = httpsCallable(fireFunctions, 'updateUserCandidateIdFromEmail');
        const result = await updateUserCandidateIdFromEmailFunction({ customerId, email });
        return result.data;
    }

    async getQuestions(jobReqId: string, locale: string) {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const getQuestionsFunction = httpsCallable(fireFunctions, 'getQuestions');

        const result = await getQuestionsFunction({ customerId, jobReqId, locale });

        return result.data as Question[];
    }

    async getStatusList() {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const getStatusFunction = httpsCallable(fireFunctions, 'getStatusList');

        const result = await getStatusFunction({ customerId });

        return result.data;
    }

    async applyToJob(jobReqId: string, data: any, isExistingCandidate?: boolean): Promise<{ applicationId: string, candidateId: string }> {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const applyFunction = httpsCallable(fireFunctions, 'applyToJob', { timeout: 540000 });
        const auth = getAuth();
        const locale = (window as any).csb.job.language;

        try {
            const result = await applyFunction({ customerId, jobData: data, jobReqId, locale: locale, isExistingCandidate: isExistingCandidate }) as any;
            if (result) {
                const resultData = result.data as { applicationId: string, candidateId: string };
                if (resultData && result.data.code) {
                    if (result.data.code === 401 || result.data.code === 'unauthenticated') {
                        //refresh token, then try again
                        globalTrackingService.trackException(new Error(`Apply to job (unauthenticated)`), 4, { applyToJobResult: result, customerId: customerId, locale: locale, jobReqId: jobReqId, data: data, currentUser: auth.currentUser });
                        await AuthService.refreshToken();
                    } else {
                        throw result.data;
                    }
                }
            }
            return result.data as any; // as { applicationId: application.applicationId, candidateId: candidate.candidateId };

        } catch (error: any) {
            if (error.code && (error.code === 401 || error.code === 'unauthenticated')) {
                globalTrackingService.trackException(error, 4, { message: `Apply to job (error 401)`, error: error, customerId: customerId, locale: locale, jobReqId: jobReqId, data: data, currentUser: auth.currentUser });
                await AuthService.refreshToken();
                throw error;
            }
            else {
                globalTrackingService.trackException(error, 4, { message: `Apply to job (error)`, error: error, customerId: customerId, locale: locale, jobReqId: jobReqId, data: data, currentUser: auth.currentUser });

                if (auth.currentUser) {
                    await auth.currentUser.getIdTokenResult(true);
                }
                throw error;
            }
        }
    }

    async checkCandidateExists(customerId: string, email: string) {
        const fireFunctions = getFunctions();
        const checkCandidateExistsFunction = httpsCallable(fireFunctions, 'checkCandidateExists');

        const result = await checkCandidateExistsFunction({ customerId, email });

        return result.data;
    }

    async createProfile(data: any) {
        const customerId = this.getCustomerId();
        let locale = CsbHelper.getLocale();

        if (locale !== 'de_DE' && locale !== 'en_US') {
            locale = 'de_DE'; //only allow de or en => fallback to de
        }
        const fireFunctions = getFunctions();
        const createProfileFunction = httpsCallable(fireFunctions, 'createProfile', { timeout: 540000 });

        const result = await createProfileFunction({ customerId, jobData: data, locale });

        return result.data;
    }

    async updateProfile(data: any, applicationIds: string[] = []) {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const applyFunction = httpsCallable(fireFunctions, 'updateProfile', { timeout: 540000 });

        const result = await applyFunction({ customerId, profile: data, applicationIds });
        return result.data;
    }

    async updateApplication(data: any, applicationId: string, applicationIds: string[] = [], filesToDelete: { folder: string, attachmentId: string }[]) {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const updateApplicationFunction = httpsCallable(fireFunctions, 'updateApplication', { timeout: 540000 });

        const result = await updateApplicationFunction({ customerId, jobData: data, applicationId, filesToDelete: filesToDelete, applicationIds });

        return result.data;
    }

    async reinstateApplication(applicationId: string) {
        let customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const reinstateFunction = httpsCallable(fireFunctions, 'reinstateJobApplication');

        let result = await reinstateFunction({ customerId, applicationId });

        return result.data;
    }

    async retractApplication(applicationId: string) {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const retractFunction = httpsCallable(fireFunctions, 'retractJobApplication');

        const result = await retractFunction({ customerId, applicationId });

        return result.data;
    }

    async getProfile(): Promise<Profile> {
        const customerId = this.getCustomerId();
        const fireFunctions = getFunctions();
        const locale = CsbHelper.getLocale();
        const getProfileFunction = httpsCallable(fireFunctions, 'getProfile', { timeout: 540000 });

        const result = await getProfileFunction({ customerId, locale });

        return result.data as Profile;
    }

    async downloadFileByUrl(url: string) {
        let functionsUrl: string = getConfigValue('functionsUrl');

        if (!functionsUrl.endsWith('/')) {
            functionsUrl += '/';
        }

        await AuthService.refreshToken();
        const response = await fetch(functionsUrl + url, {
            headers: {
                'Authorization': 'Bearer ' + AuthService.token
            }
        });
        let filename: string = 'document';
        const contentHeader = response.headers.get('Content-Disposition');
        if (contentHeader && contentHeader.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentHeader);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        const blob = await response.blob();
        // var file = window.URL.createObjectURL(blob);
        // window.location.assign(file);

        saveAs(blob, filename, { autoBom: true });
    }
}

export const ApplyService = new ApplyServiceController();